/**
 * Auth Feature - Session Cleanup Utility
 *
 * Provides a single function to nuke ALL session data from the browser.
 *
 * This MUST be called before initiating any OAuth redirect so that
 * a new user's session starts from a guaranteed clean slate.
 * Without this, data from a previous user (org ID, tokens, user-data cookie)
 * can survive the redirect round-trip and pollute the new session.
 *
 * Storage layers cleared:
 * 1. Cookies:       auth-token, refresh-token, user-data
 * 2. localStorage:  organizationId, organizationName, orgType + any other keys
 * 3. sessionStorage: full clear
 * 4. React Query:   cache invalidation (caller must pass queryClient)
 */

import { storageClient } from "@/shared/core/clients";
import { COOKIE_NAMES } from "@/constants";
import {
  clearSelectedOrganization,
} from "@/shared/core/utils";

// ============================================================================
// CORE CLEANUP — synchronous, no React dependencies
// ============================================================================

/**
 * Wipe every piece of session data from the browser.
 *
 * Call this:
 *  - Immediately before `window.location.href = oauthUrl` (in OAuthButton)
 *  - At the start of TokenCallbackHandler (belt-and-suspenders)
 *
 * This function is intentionally NOT a React hook so it can be called from
 * any context, including plain event handlers and non-React utility code.
 */
export function clearAllSessionData(): void {
  // ── 1. Auth cookies ────────────────────────────────────────────────────
  storageClient.deleteCookie(COOKIE_NAMES.AUTH_TOKEN);
  storageClient.deleteCookie(COOKIE_NAMES.REFRESH_TOKEN);
  storageClient.deleteCookie(COOKIE_NAMES.USER_DATA);

  // ── 2. Organization context in localStorage ────────────────────────────
  // clearSelectedOrganization removes: organizationId, organizationName, orgType
  clearSelectedOrganization();

  // ── 3. Nuke remaining localStorage (recent searches, cached state, etc.)
  if (typeof window !== "undefined") {
    try {
      window.localStorage.clear();
    } catch {
      // Ignore — storage may be unavailable in some environments
    }
  }

  // ── 4. Nuke sessionStorage ─────────────────────────────────────────────
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.clear();
    } catch {
      // Ignore
    }
  }
}

/**
 * Invalidate all React Query caches.
 *
 * Call this from a component/hook context where `queryClient` is available.
 * Separated from `clearAllSessionData` because it requires the React Query
 * client, which is not available in non-React contexts.
 */
export function clearQueryCaches(
  queryClient: import("@tanstack/react-query").QueryClient,
): void {
  queryClient.clear();
}
